'use client'

import { useCallback, useEffect, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import { RecipeRatePromptModal } from '@/components/recipe/RecipeRatePromptModal'
import {
  clearPendingRecipeRatingPrompt,
  PENDING_RATING_PROMPT_EVENT,
  readPendingRecipeRatingPrompt,
  type PendingRecipeRatingPrompt,
} from '@/lib/pendingRecipeRatingPrompt'

const SHOULD_PROMPT_RECIPE_RATING = gql`
  query ShouldPromptRecipeRatingHost($recipe_id: Int!) {
    shouldPromptRecipeRating(recipe_id: $recipe_id)
  }
`

const DISMISS_RECIPE_RATING_PROMPT = gql`
  mutation DismissRecipeRatingPromptHost($recipe_id: Int!) {
    dismissRecipeRatingPrompt(recipe_id: $recipe_id)
  }
`

/**
 * Shows a queued rating prompt after the user leaves a recipe (Cook Mode ≥ 3 min).
 * Lives in the root layout so the modal survives RecipeDetails unmount.
 */
export function RecipeRatingPromptHost() {
  const pathname = usePathname()
  const router = useRouter()
  const [pending, setPending] = useState<PendingRecipeRatingPrompt | null>(null)
  const [open, setOpen] = useState(false)

  const [checkShouldPrompt] = useLazyQuery(SHOULD_PROMPT_RECIPE_RATING, {
    fetchPolicy: 'no-cache',
  })
  const [dismissPrompt] = useMutation(DISMISS_RECIPE_RATING_PROMPT)

  const tryShowPending = useCallback(async () => {
    const next = readPendingRecipeRatingPrompt()
    if (!next?.recipe?.recipe_id) return

    // Don't show while still on that recipe page — RecipeDetails owns in-page prompt.
    if (pathname?.startsWith(`/recipes/${next.recipe.slug}`)) return

    try {
      const res = await checkShouldPrompt({
        variables: { recipe_id: next.recipe.recipe_id },
      })
      if (!res.data?.shouldPromptRecipeRating) {
        clearPendingRecipeRatingPrompt()
        return
      }
      setPending(next)
      setOpen(true)
    } catch {
      clearPendingRecipeRatingPrompt()
    }
  }, [pathname, checkShouldPrompt])

  useEffect(() => {
    void tryShowPending()
  }, [tryShowPending, pathname])

  useEffect(() => {
    const onQueued = () => {
      void tryShowPending()
    }
    window.addEventListener(PENDING_RATING_PROMPT_EVENT, onQueued)
    return () => window.removeEventListener(PENDING_RATING_PROMPT_EVENT, onQueued)
  }, [tryShowPending])

  const closeAndClear = useCallback(() => {
    setOpen(false)
    setPending(null)
    clearPendingRecipeRatingPrompt()
  }, [])

  const skip = useCallback(async () => {
    const recipeId = pending?.recipe?.recipe_id
    closeAndClear()
    if (!recipeId) return
    try {
      await dismissPrompt({ variables: { recipe_id: recipeId } })
    } catch {
      // local close still happens
    }
  }, [pending, closeAndClear, dismissPrompt])

  const rate = useCallback(() => {
    const slug = pending?.recipe?.slug
    closeAndClear()
    if (!slug) return
    router.push(`/recipes/${slug}#recipe-reviews`)
  }, [pending, closeAndClear, router])

  const dismissWithoutSnooze = useCallback(() => {
    closeAndClear()
  }, [closeAndClear])

  if (!pending?.recipe) return null

  return (
    <RecipeRatePromptModal
      open={open}
      recipe={pending.recipe}
      onSkip={skip}
      onRate={rate}
      onDismissWithoutSnooze={dismissWithoutSnooze}
    />
  )
}
